// ReferWelcomePanel — the Refer & Save section's Welcome tab (account-area
// redesign Task 10, amendment O5/F11): a sales page — hero, headline, the
// three-step how-it-works, the reward explanation, one CTA into the refer
// form. It replaces the refer-form intro card as the section's landing tab.
//
// COUNSEL-REVIEW: every static string in this file is program copy — the
// complete block is collected verbatim in the task report for counsel review.
// Two rules bound the writing:
//   - the reward explanation is NEVER hard-coded: amounts and terms come
//     from the CONFIGURED enrollment disclosure, rendered verbatim; when no
//     terms are configured (404/malformed) the Reward details block is
//     omitted entirely — an unconfigured program must not ship invented
//     terms;
//   - banned vocabulary (operator ruling): "free", "Aldelo Cloud",
//     "Payouts" appear nowhere; "Refer & Save" is the program's exact name.
//
// The subhead and step 1 reuse the shipped (counsel-reviewed) refer-tab
// intro verbatim; the hero reuses the marketplace CDN artwork (O5 left the
// asset supplier open — flagged in the task report) with a descriptive alt,
// lazy loading and a 1200px cap (O5's constraints).
//
// YOUR SHARE LINK (account-area redesign Task 20 slice 2, migration 147):
// the Welcome tab's attributed-link block. The PARENT (ReferPanel) owns the
// single GET /me/referral-share-link read and passes the block's state down —
// this panel never refetches, and the same resolved link feeds the refer
// tab's composed message. Creating is the block's explicit button (the POST
// is idempotent server-side — an existing active link comes back unchanged,
// so a double-click cannot churn the token). The share intents reuse slice
// 1's ShareIntentLink shape (share-intent.tsx — the hoisted anchor-styled
// control; EmbedLink is button-only by design, TD-329) with the URL alone as
// the payload — no invented copy.
import { useCallback, useEffect, useState } from "react";
import {
  EmbedBanner,
  EmbedButton,
  EmbedCard,
  EmbedHeading,
  EmbedText,
  useEmbedTokens,
} from "../../embed-shared/primitives";
import { getActiveReferralDisclosure } from "./referral-api";
import {
  ENROLLMENT_ACCEPT_HINT,
  ENROLLMENT_UNAVAILABLE_MESSAGE,
  EnrollmentAcceptance,
  enrollmentAccepted,
  type EnrollmentGateState,
} from "./enrollment-gate";
import { ShareIntentLink, mailtoHref, smsHref } from "./share-intent";

/** The "Your share link" block's state, owned by the parent section host
 *  (ReferPanel) and passed down. `url` is the FULL share URL (origin + path +
 *  `?ref=<token>`) the parent derived from the live page — never the bare
 *  token. */
export interface ShareLinkBlockState {
  status: "loading" | "none" | "ready" | "error";
  url: string | null;
  error?: string | null;
  /** TD-765: the link exists but is a LEGACY UNACCEPTED one (wire
   *  `enrollment_disclosure_id === null`) — its public leads are refused until the owner
   *  accepts the program terms. NEVER set for an ABSENT wire field (an old
   *  server that predates the acceptance triple): that link works, so no
   *  prompt. */
  needsAcceptance?: boolean;
}

// O5: the marketplace hero artwork already served from the embeds' CDN
// (verified image/jpeg 2048x738). Task owner may swap the asset without
// touching this file — the URL is the only binding.
const WELCOME_HERO_URL = "https://storefront-js.aldelo.ai/marketplace-hero-bg.jpg";
const WELCOME_HERO_ALT =
  "Abstract illustration of translucent glass panels in soft blue and orange light";

const STEPS: { title: string; body: string }[] = [
  {
    title: "Refer a store",
    body: "Introduce us to a business that could benefit from Aldelo.",
  },
  {
    title: "They join",
    body: "Aldelo's team reaches out and helps the store get set up.",
  },
  {
    title: "You save on your statement",
    body: "When the referral completes, referral credits are applied to your processing statements.",
  },
];

// The program headline is the ONE vocabulary for the program's tagline: this
// panel's hero renders it, and the refer tab's composed share message composes
// it (Task 20 slice 1). Sourced here deliberately — no CONFIG-sourced tagline
// exists (referral-api.ts exposes only disclosures/legal text; the portal's
// brand_tagline is operator-side and deliberately absent from the public
// settings the embed reads), so the message composes from the Welcome tab's
// own program copy.
export const REFER_WELCOME_HEADLINE = "Refer a store, earn referral credits";

export interface ReferWelcomePanelProps {
  /** Opens the refer form (the section host switches to its refer tab). */
  onReferClick?: () => void;
  /** Slice 2: the "Your share link" block's parent-owned state. Absent
   *  (undefined) renders no block — the standalone shape the original tests
   *  exercise; the section host always passes it. */
  shareLink?: ShareLinkBlockState;
  /** I-1: the store read settled and the caller has no Aldelo Pay store (a
   *  live store they own with an active, live MID linked to it) — the
   *  server's public-lead gate refuses every lead for such a referrer (the
   *  uniform 404), so the block shows the Merchant services pointer instead
   *  of a link. An active MID with no store does NOT qualify. */
  shareLinkUnavailable?: boolean;
  /** D6a (merge-review M1, operator answer "d6a = create"): the confirmed
   *  "Get a new link" action. Resolves true on success. The parent owns the
   *  request, the busy flag and the error; the confirm step is local. */
  onRotateShareLink?: () => Promise<boolean>;
  rotatingShareLink?: boolean;
  /** Set when the last rotate failed — shown beside the KEPT old link. */
  rotateShareLinkError?: string | null;
  /** True right after a successful rotate (a polite status line). */
  shareLinkRotated?: boolean;
  /** Creates the caller's link (idempotent POST — see the header). */
  onCreateShareLink?: () => void;
  /** Re-runs the block's read after a failure. */
  onRetryShareLink?: () => void;
  /** TD-765: the section-owned enrollment-disclosure gate. When PROVIDED, the
   *  panel renders the acceptance checkbox under the shown terms and disables
   *  create / "Get a new link" / activate until the terms are accepted. When
   *  absent (the standalone shape) the panel fetches and shows the terms
   *  itself and gates nothing. */
  enrollment?: EnrollmentGateState;
  onEnrollmentAcceptChange?: (accepted: boolean) => void;
  onRetryEnrollment?: () => void;
  /** Activates a legacy unaccepted link: re-POSTs WITHOUT rotating (the
   *  server stamps the link and keeps its token). */
  onActivateShareLink?: () => void;
}

export function ReferWelcomePanel({
  onReferClick,
  shareLink,
  shareLinkUnavailable,
  onCreateShareLink,
  onRetryShareLink,
  onRotateShareLink,
  rotatingShareLink = false,
  rotateShareLinkError = null,
  shareLinkRotated = false,
  enrollment,
  onEnrollmentAcceptChange,
  onRetryEnrollment,
  onActivateShareLink,
}: ReferWelcomePanelProps) {
  const t = useEmbedTokens();
  // The CONFIGURED enrollment terms — the only source of the reward
  // explanation. Null until a valid body arrives; a 404/malformed payload
  // leaves it omitted (never invented copy).
  const [termsBody, setTermsBody] = useState<string | null>(null);
  // The share-link block's copy confirmation (the ReferPanel idiom: a
  // self-clearing flag, and a refused clipboard write stays silent).
  const [linkCopied, setLinkCopied] = useState(false);
  // The in-page confirm step for "Get a new link" (the embed viewer has no
  // confirm()). Local UI state only.
  const [confirmingRotate, setConfirmingRotate] = useState(false);

  const handleCopyLink = useCallback(() => {
    if (!shareLink?.url) return;
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(shareLink.url).then(
      () => setLinkCopied(true),
      () => setLinkCopied(false),
    );
  }, [shareLink?.url]);

  useEffect(() => {
    if (!linkCopied) return;
    const timer = window.setTimeout(() => setLinkCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [linkCopied]);

  // TD-765: with a section-owned gate the terms come from it (one fetch, one
  // acceptance flag); the standalone shape keeps its own read.
  const gated = enrollment !== undefined;
  const canAct = !gated || enrollmentAccepted(enrollment);
  const shownTerms = gated ? (enrollment.disclosure?.body ?? null) : termsBody;

  useEffect(() => {
    if (gated) return;
    let cancelled = false;
    getActiveReferralDisclosure("enrollment")
      .then((d) => {
        if (!cancelled && typeof d?.body === "string" && d.body.length > 0) {
          setTermsBody(d.body);
        }
      })
      .catch(() => {
        // No configured terms → no reward block (the omission is the
        // contract; see the header).
      });
    return () => {
      cancelled = true;
    };
  }, [gated]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
      <EmbedCard>
        <img
          src={WELCOME_HERO_URL}
          alt={WELCOME_HERO_ALT}
          loading="lazy"
          width={1200}
          height={432}
          style={{ width: "100%", maxWidth: 1200, height: "auto", display: "block", borderRadius: t.borderRadius }}
        />
        <div style={{ marginTop: t.spacing.md, display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
          <EmbedText size="xs" tone="secondary">Refer & Save</EmbedText>
          <EmbedHeading level={2}>{REFER_WELCOME_HEADLINE}</EmbedHeading>
          <EmbedText tone="secondary">
            Introduce us to a business that could benefit from Aldelo. If
            your referral becomes a customer, you earn referral credits
            applied to your processing statements.
          </EmbedText>
          <div>
            <EmbedButton type="button" onClick={onReferClick}>
              Refer a store
            </EmbedButton>
          </div>
        </div>
      </EmbedCard>

      <EmbedCard>
        <EmbedHeading level={3}>How it works</EmbedHeading>
        <ol style={{ margin: `${t.spacing.sm}px 0 0`, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: t.spacing.md }}>
          {STEPS.map((step, i) => (
            <li key={step.title} style={{ display: "flex", gap: t.spacing.md, alignItems: "baseline" }}>
              <span
                aria-hidden="true"
                style={{
                  flex: "0 0 auto",
                  width: 28,
                  height: 28,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  background: t.colors.primaryTint,
                  color: t.colors.primary,
                  fontSize: t.fontSize.sm,
                  fontWeight: 600,
                }}
              >
                {i + 1}
              </span>
              <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: t.fontSize.sm, fontWeight: 600, color: t.colors.text }}>{step.title}</span>
                <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>{step.body}</span>
              </span>
            </li>
          ))}
        </ol>
      </EmbedCard>

      {/* The reward explanation — the CONFIGURED terms verbatim, omitted
          entirely when none are configured (never invented copy, never a
          hard-coded amount). */}
      {shownTerms !== null && shownTerms.length > 0 && (
        <EmbedCard>
          <EmbedHeading level={3}>Reward details</EmbedHeading>
          <div
            data-testid="welcome-terms-body"
            style={{
              marginTop: t.spacing.sm,
              whiteSpace: "pre-wrap",
              fontSize: t.fontSize.sm,
              lineHeight: t.lineHeight.normal,
              color: t.colors.text,
            }}
          >
            {shownTerms}
          </div>
          {gated && enrollment.status === "ready" && onEnrollmentAcceptChange && (
            <div style={{ marginTop: t.spacing.md }}>
              <EnrollmentAcceptance
                id="welcome-enrollment-accept"
                accepted={enrollment.accepted}
                onChange={onEnrollmentAcceptChange}
              />
            </div>
          )}
        </EmbedCard>
      )}

      {/* YOUR SHARE LINK (slice 2): the parent-owned state renders one of
          four arms. The URL is the full ?ref= link the parent derived from
          the live page; the share intents carry the URL ALONE as their
          payload — no invented copy. */}
      {shareLinkUnavailable && (
        <EmbedCard>
          <EmbedHeading level={3}>Your share link</EmbedHeading>
          <p style={{ margin: `${t.spacing.sm}px 0 0` }}>
            <EmbedText tone="secondary">
              Your share link needs an Aldelo Pay store: a store with an
              active merchant account linked to it. Set one up under
              Merchant services — your share link is available once Aldelo
              verifies it.
            </EmbedText>
          </p>
        </EmbedCard>
      )}
      {shareLink !== undefined && (
        <EmbedCard>
          <EmbedHeading level={3}>Your share link</EmbedHeading>
          {gated && enrollment.status === "loading" && (
            <p role="status" style={{ margin: `${t.spacing.sm}px 0 0` }}>
              <EmbedText tone="secondary">Loading the program terms...</EmbedText>
            </p>
          )}
          {gated && enrollment.status === "unavailable" && (
            <p role="status" style={{ margin: `${t.spacing.sm}px 0 0` }}>
              <EmbedText tone="secondary">{ENROLLMENT_UNAVAILABLE_MESSAGE}</EmbedText>
            </p>
          )}
          {gated && enrollment.status === "error" && (
            <div style={{ marginTop: t.spacing.sm, display: "flex", flexDirection: "column", gap: t.spacing.sm, alignItems: "flex-start" }}>
              <EmbedBanner tone="error">{enrollment.error ?? "Failed to load the program terms"}</EmbedBanner>
              {onRetryEnrollment && (
                <EmbedButton type="button" variant="secondary" size="sm" onClick={onRetryEnrollment}>
                  Retry
                </EmbedButton>
              )}
            </div>
          )}
          {gated && enrollment.status === "ready" && !enrollment.accepted && shareLink.status !== "loading" && shareLink.status !== "error" && (
            <p style={{ margin: `${t.spacing.sm}px 0 0` }}>
              <EmbedText tone="secondary">{ENROLLMENT_ACCEPT_HINT}</EmbedText>
            </p>
          )}
          {shareLink.status === "loading" && (
            <p role="status" style={{ margin: `${t.spacing.sm}px 0 0` }}>
              <EmbedText tone="secondary">Loading your share link...</EmbedText>
            </p>
          )}
          {shareLink.status === "none" && (
            <div style={{ marginTop: t.spacing.sm, display: "flex", flexDirection: "column", gap: t.spacing.sm, alignItems: "flex-start" }}>
              <EmbedText tone="secondary">
                Create a personal link to this page — stores that find us
                through it are attributed to you.
              </EmbedText>
              {shareLink.error && <EmbedBanner tone="error">{shareLink.error}</EmbedBanner>}
              <EmbedButton type="button" variant="secondary" disabled={!canAct} onClick={onCreateShareLink}>
                Create your share link
              </EmbedButton>
            </div>
          )}
          {shareLink.status === "error" && (
            <div style={{ marginTop: t.spacing.sm, display: "flex", flexDirection: "column", gap: t.spacing.sm, alignItems: "flex-start" }}>
              <EmbedBanner tone="error">{shareLink.error ?? "Failed to load your share link"}</EmbedBanner>
              <EmbedButton type="button" variant="secondary" onClick={onRetryShareLink}>
                Retry
              </EmbedButton>
            </div>
          )}
          {shareLink.status === "ready" && shareLink.url && shareLink.needsAcceptance && (
            // TD-765: a LEGACY unaccepted link. Its leads are refused, so the
            // URL and the share actions are withheld until the owner accepts;
            // activating re-POSTs without rotating, so the address a member
            // already shared starts working again.
            <div style={{ marginTop: t.spacing.sm, display: "flex", flexDirection: "column", gap: t.spacing.sm, alignItems: "flex-start" }}>
              <EmbedText tone="secondary">
                Your share link is not active yet. Accept the program terms to
                activate it. The link address stays the same.
              </EmbedText>
              {shareLink.error && <EmbedBanner tone="error">{shareLink.error}</EmbedBanner>}
              <EmbedButton type="button" variant="primary" size="sm" disabled={!canAct || !onActivateShareLink} onClick={onActivateShareLink}>
                Accept terms and activate link
              </EmbedButton>
            </div>
          )}
          {shareLink.status === "ready" && shareLink.url && !shareLink.needsAcceptance && (
            <div style={{ marginTop: t.spacing.sm, display: "flex", flexDirection: "column", gap: t.spacing.sm }}>
              <div
                data-testid="share-link-url"
                style={{
                  fontFamily: t.fontFamilyMono,
                  fontSize: t.fontSize.xs,
                  color: t.colors.text,
                  background: t.colors.surface,
                  border: `1px solid ${t.colors.border}`,
                  borderRadius: t.borderRadiusControl,
                  padding: `${t.spacing.xs}px ${t.spacing.sm}px`,
                  wordBreak: "break-all",
                }}
              >
                {shareLink.url}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: t.spacing.sm }}>
                <EmbedButton type="button" variant="secondary" size="sm" onClick={handleCopyLink}>
                  {linkCopied ? "Copied" : "Copy link"}
                </EmbedButton>
                <ShareIntentLink href={mailtoHref(shareLink.url)}>Email</ShareIntentLink>
                <ShareIntentLink href={smsHref(shareLink.url)}>Text</ShareIntentLink>
                {onRotateShareLink && !confirmingRotate && (
                  <EmbedButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={rotatingShareLink || !canAct}
                    onClick={() => setConfirmingRotate(true)}
                  >
                    Get a new link
                  </EmbedButton>
                )}
              </div>
              {shareLinkRotated && !rotateShareLinkError && !confirmingRotate && (
                <p role="status" style={{ margin: 0 }}>
                  <EmbedText tone="secondary">New link ready. The old link no longer works.</EmbedText>
                </p>
              )}
              {rotateShareLinkError && <EmbedBanner tone="error">{rotateShareLinkError}</EmbedBanner>}
              {confirmingRotate && (
                // OPERATOR-REVIEW DRAFT COPY (listed in the M1 report).
                <div
                  role="group"
                  aria-label="Get a new link?"
                  style={{ display: "flex", flexDirection: "column", gap: t.spacing.sm }}
                >
                  <EmbedText>
                    Your current link will stop working right away. Anyone who opens it will see
                    &ldquo;Referral link not found&rdquo;, so replace it wherever you have shared it.
                  </EmbedText>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: t.spacing.sm }}>
                    <EmbedButton
                      type="button"
                      variant="primary"
                      size="sm"
                      disabled={rotatingShareLink || !canAct}
                      loading={rotatingShareLink}
                      onClick={async () => {
                        const ok = await onRotateShareLink?.();
                        if (ok !== undefined) setConfirmingRotate(false);
                      }}
                    >
                      Yes, get a new link
                    </EmbedButton>
                    <EmbedButton
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={rotatingShareLink}
                      onClick={() => setConfirmingRotate(false)}
                    >
                      Keep current link
                    </EmbedButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </EmbedCard>
      )}
    </div>
  );
}
